// Settings, Statuses, Urgency and window, Defaults (spec C4, C5, C14; Settings board): the rules the
// portal runs today, read only. Editing them comes before go-live (shell/placeholders.ts), so each
// page says plainly that it is read only and shows its edit control disabled with the reason.
import { useCallback, useState, type ReactNode } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import type { Api, FollowUpView, IncreaseRuleView } from '../auth/api';
import { FollowUpEditor } from '../settings/FollowUpEditor';
import { followUpCopy } from '../settings/follow-up-lines';
import { IncreaseRuleEditor } from '../settings/IncreaseRuleEditor';
import { increaseCopy } from '../settings/increase-rule-lines';
import { Toast } from '../shell/Toast';
import { increaseRuleText } from '../renewals/renewal-lines';
import { PlaceholderButton } from '../shell/Placeholder';
import type { PlaceholderId } from '../shell/placeholders';
import { PageHeader } from '../shell/PageHeader';
import { InfoIcon, LockIcon, PlusIcon } from '../shell/icons';
import { SettingsNav } from '../settings/SettingsNav';
import {
  attentionRules,
  bandRows,
  fetching,
  increase,
  statusRows,
  windows,
} from '../settings/department-defaults';

function Shell({
  path,
  title,
  edit,
  banner = 'Read only for now. These are the rules the portal runs today.',
  children,
}: {
  path: string;
  title: string;
  edit: { id: PlaceholderId; label: ReactNode };
  /** The info line under the title; Defaults says the increase rule can be changed. */
  banner?: string;
  children: ReactNode;
}) {
  return (
    <>
      <PageHeader title="Settings" subtitle="Changes apply to everyone in the department" />
      <div className="setwrap">
        <SettingsNav current={path} />
        <div className="setcol">
          <div className="row g12 between">
            <h2 className="dtitle">{title}</h2>
            <PlaceholderButton id={edit.id} className="btn">
              {edit.label}
            </PlaceholderButton>
          </div>
          <div className="bnr info">
            <InfoIcon />
            <span>{banner}</span>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

/** A row is [label, value], plus what opens its editor when this person may change it. */
type Row = [string, ReactNode] | [string, ReactNode, { name: string; open: () => void }];

function Rows({ title, rows }: { title: string; rows: Row[] }) {
  return (
    <section className="card">
      <div className="card-h">
        <h3 className="card-t">{title}</h3>
      </div>
      <dl className="m0">
        {rows.map(([label, value, edit]) => (
          // An editable row opens its editor from anywhere on the row (kit SettingsEditor).
          <div key={label} className={edit ? 'kv trow' : 'kv'}>
            <dt>{label}</dt>
            <dd>
              {edit ? (
                <button
                  type="button"
                  className="ins"
                  aria-haspopup="dialog"
                  aria-label={`Edit ${edit.name}: ${String(value)}`}
                  onClick={edit.open}
                >
                  {value}
                </button>
              ) : (
                value
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const statusColumns = 'minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 2fr) max-content';

export function StatusesSettingsPage() {
  return (
    <Shell
      path="/settings/statuses"
      title="Statuses"
      edit={{
        id: 'settings.addStatus',
        label: (
          <>
            <PlusIcon />
            Add status
          </>
        ),
      }}
    >
      <p className="m0 t13 muted">
        Statuses match the department sheet. The 9 built-in statuses keep their stage.
      </p>
      <div className="hdr" style={{ gridTemplateColumns: statusColumns }}>
        {['Label', 'Stage', 'Sheet value', 'Built-in'].map((h) => (
          <div key={h} className="th t12 muted w5">
            <span className="ell">{h}</span>
          </div>
        ))}
      </div>
      <div className="gcard">
        {statusRows.map((s) => (
          <div
            key={s.key}
            className="trow"
            style={{ gridTemplateColumns: statusColumns, cursor: 'default' }}
          >
            <div className="td vis">
              <span className={`pill ${s.pill}`}>{s.label}</span>
            </div>
            <div className="td text2">{s.stage}</div>
            <div className="td mono">
              <span className="ell">{s.sheet}</span>
            </div>
            <div className="td muted" aria-label="Built-in">
              <LockIcon className="ico s14" />
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

const days = (n: number) => `${n} days`;

export function UrgencySettingsPage() {
  return (
    <Shell
      path="/settings/urgency"
      title="Urgency and window"
      edit={{ id: 'settings.editUrgency', label: 'Edit' }}
    >
      <Rows
        title="Windows"
        rows={[
          ['Review window', `${days(windows.review)}: the AE sees renewals from here`],
          ['Working window', `${days(windows.working)}: renewals enter the VA queues`],
          ['Missed filter', `${days(windows.missed)}: expired records that are not closed stay`],
        ]}
      />
      <Rows
        title="Bands"
        rows={bandRows.map((b) => [
          b.label,
          <span key={b.label} className={b.pill}>
            {b.days}
          </span>,
        ])}
      />
      <section className="card">
        <div className="card-h">
          <h3 className="card-t">Needs attention</h3>
        </div>
        <div>
          {attentionRules.map((rule) => (
            <div key={rule} className="frow2 t13">
              <span className="grow ink">{rule}</span>
              <span className="text2">On</span>
            </div>
          ))}
        </div>
      </section>
      <p className="m0 t12 muted">
        Records that match go to the top of the queue under Needs attention.
      </p>
    </Shell>
  );
}

const notSet = <span className="faint">Not set yet</span>;

export type DefaultsLoaderData = { increase: IncreaseRuleView; followUp: FollowUpView };

export function DefaultsSettingsPage({ api }: { api: Api }) {
  const { increase: increaseView, followUp } = useLoaderData() as DefaultsLoaderData;
  const { rule, canEdit } = increaseView;
  const revalidator = useRevalidator();
  const [editing, setEditing] = useState<'increase' | 'followUp' | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const saved = useCallback(
    (text: string) => {
      setEditing(null);
      setToast({ id: Date.now(), text });
      revalidator.revalidate();
    },
    [revalidator],
  );
  const threshold = `Premium up ${increaseRuleText(rule)}`;
  return (
    <>
      <Shell
        path="/settings/defaults"
        title="Defaults"
        edit={{ id: 'settings.editDefaults', label: 'Edit' }}
        banner="Only the increase rule and the follow-up days can be changed for now. The other defaults become editable with Settings."
      >
        <Rows
          title="When AMS360 is missing data"
          rows={[
            ['Department executive', notSet],
            ['Department rep', notSet],
          ]}
        />
        <Rows
          title="Increase"
          rows={[
            canEdit
              ? [
                  'Threshold',
                  threshold,
                  { name: increaseCopy.title.toLowerCase(), open: () => setEditing('increase') },
                ]
              : ['Threshold', threshold],
            [
              'Increase template',
              `Only when more than ${days(increase.templateMoreThanDays)} remain`,
            ],
          ]}
        />
        <Rows title="Non-renewal" rows={[['Compliance contact email', notSet]]} />
        <Rows
          title="Carrier fetch"
          rows={[
            ['Wait for the 2FA code', `${fetching.codeWaitSeconds} seconds`],
            ['Fetch attempts before failing', String(fetching.attempts)],
            followUp.canEdit
              ? [
                  followUpCopy.row,
                  followUpCopy.value(followUp.days),
                  {
                    name: followUpCopy.title.toLowerCase(),
                    open: () => setEditing('followUp'),
                  },
                ]
              : [followUpCopy.row, followUpCopy.value(followUp.days)],
          ]}
        />
      </Shell>
      {editing === 'increase' && (
        <IncreaseRuleEditor api={api} rule={rule} onClose={close} onSaved={saved} />
      )}
      {editing === 'followUp' && (
        <FollowUpEditor api={api} days={followUp.days} onClose={close} onSaved={saved} />
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </>
  );
}
